import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService } from '../../authentication/services/auth.service';

export type AppFontId =
  | 'default'
  | 'inter'
  | 'poppins'
  | 'manrope'
  | 'space-grotesk'
  | 'dm-sans'
  | 'plus-jakarta-sans'
  | 'outfit'
  | 'work-sans';

export interface AppFontOption {
  id: AppFontId;
  label: string;
  /** Valeur CSS de --font-sans. Vide = police système par défaut de l'application. */
  stack: string;
  /** Paramètre "family" de l'API Google Fonts CSS2 (poids inclus). Absent = police système, rien à charger. */
  googleFamily?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FontService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);

  private readonly storageKey = 'app_font_family';
  private readonly userConfigKey = 'app_font_family';
  private readonly defaultFont: AppFontId = 'default';

  readonly fonts: AppFontOption[] = [
    { id: 'default', label: 'Système (par défaut)', stack: '' },
    {
      id: 'inter', label: 'Inter',
      stack: '"Inter", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Inter:wght@400;500;600;700',
    },
    {
      id: 'poppins', label: 'Poppins',
      stack: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Poppins:wght@400;500;600;700',
    },
    {
      id: 'manrope', label: 'Manrope',
      stack: '"Manrope", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Manrope:wght@400;500;600;700',
    },
    {
      id: 'space-grotesk', label: 'Space Grotesk',
      stack: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Space+Grotesk:wght@400;500;600;700',
    },
    {
      id: 'dm-sans', label: 'DM Sans',
      stack: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'DM+Sans:wght@400;500;600;700',
    },
    {
      id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans',
      stack: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Plus+Jakarta+Sans:wght@400;500;600;700',
    },
    {
      id: 'outfit', label: 'Outfit',
      stack: '"Outfit", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Outfit:wght@400;500;600;700',
    },
    {
      id: 'work-sans', label: 'Work Sans',
      stack: '"Work Sans", ui-sans-serif, system-ui, sans-serif',
      googleFamily: 'Work+Sans:wght@400;500;600;700',
    },
  ];

  private loadedGoogleFamilies = new Set<string>();

  private currentFontSource = new BehaviorSubject<AppFontId>(this.getInitialFont());
  currentFont$ = this.currentFontSource.asObservable();

  constructor() {
    this.applyFont(this.currentFontSource.value);

    // La DB (UserConfig) est la source de vérité ; le localStorage n'est qu'un
    // cache de rendu rapide pour éviter le flash de police au chargement.
    this.authService.user.pipe(
      filter((user): user is NonNullable<typeof user> => !!user),
      take(1),
    ).subscribe(user => {
      const dbFont = user?.config?.[this.userConfigKey] as string | undefined;
      if (dbFont) {
        this.setFont(this.normalize(dbFont), true);
      }
    });
  }

  getCurrentFont(): AppFontId {
    return this.currentFontSource.value;
  }

  /**
   * Charge toutes les feuilles Google Fonts pour prévisualiser chaque police
   * dans son propre rendu (sélecteur de police du gestionnaire de thèmes).
   * À n'appeler que depuis un écran de réglages ouvert volontairement : ce
   * n'est pas le comportement par défaut au chargement de l'app.
   */
  preloadFontPreviews(): void {
    this.fonts.forEach(font => {
      if (font.googleFamily) {
        this.ensureGoogleFontLoaded(font.googleFamily);
      }
    });
  }

  setFont(id: AppFontId, fromRestore = false): void {
    const next = this.normalize(id);
    if (next === this.currentFontSource.value) return;

    this.currentFontSource.next(next);
    // Pas de fondu pour une restauration silencieuse au chargement (DB vs
    // cache localStorage) : seul un changement explicite en interface doit
    // se voir.
    this.applyFont(next, !fromRestore);
    localStorage.setItem(this.storageKey, next);

    if (!fromRestore) {
      this.persistFont(next);
    }
  }

  private normalize(id: string | null | undefined): AppFontId {
    return this.fonts.some(f => f.id === id) ? (id as AppFontId) : this.defaultFont;
  }

  private getInitialFont(): AppFontId {
    return this.normalize(
      localStorage.getItem(this.storageKey)
      || this.authService.user.value?.config?.[this.userConfigKey]
    );
  }

  /**
   * Surcharge la variable CSS --font-sans définie par Tailwind (theme.css) sur
   * :root. Toute l'app (preflight + classes utilitaires font-sans) lit cette
   * variable via var(--font-sans), donc l'override cascade partout sans avoir
   * à toucher tailwind.config.js.
   */
  private applyFont(id: AppFontId, withTransition = false): void {
    const font = this.fonts.find(f => f.id === id);

    const setVariable = () => {
      const root = document.documentElement;
      if (font?.googleFamily) {
        this.ensureGoogleFontLoaded(font.googleFamily);
      }
      if (font?.stack) {
        root.style.setProperty('--font-sans', font.stack);
      } else {
        root.style.removeProperty('--font-sans');
      }
    };

    if (withTransition) {
      void this.transitionFontChange(setVariable, font);
    } else {
      setVariable();
    }
  }

  /** Durée (ms) de la transition d'opacité/flou définie sur .font-swap-veil dans styles.css. */
  private static readonly VEIL_FADE_MS = 220;

  /**
   * font-family n'est pas interpolable en CSS : au lieu d'essayer de
   * transitionner la police, on masque le reflow brutal de tout le texte de
   * l'app derrière un fondu (opacité + flou léger). Le changement lui-même
   * n'est appliqué qu'une fois le fondu sortant terminé, pour que le "saut"
   * de mise en page reste invisible sous le voile. Classes définies
   * globalement dans styles.css (portée body, pas seulement ce composant).
   */
  private async transitionFontChange(applyChange: () => void, font?: AppFontOption): Promise<void> {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyChange();
      return;
    }

    const body = document.body;
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    body.classList.add('font-swap-veil');
    await new Promise(requestAnimationFrame);
    body.classList.add('font-swap-veil-active');

    // Laisse le fondu sortant se terminer avant de toucher au DOM : le reflow
    // se produit pendant que l'app est déjà masquée.
    await delay(FontService.VEIL_FADE_MS);
    applyChange();

    // Laisse la police le temps de charger (plafonné), avec un plancher pour
    // que le voile reste visible assez longtemps pour être perçu comme une
    // transition volontaire plutôt qu'un simple flicker.
    await Promise.all([
      delay(140),
      Promise.race([this.waitForFontFace(font), delay(500)]),
    ]);

    body.classList.remove('font-swap-veil-active');
    await delay(FontService.VEIL_FADE_MS + 40);
    body.classList.remove('font-swap-veil');
  }

  /** Attend que la police (si Google Font) soit effectivement chargée par le navigateur. */
  private waitForFontFace(font?: AppFontOption): Promise<unknown> {
    if (!font?.stack || !('fonts' in document)) return Promise.resolve();
    const primaryFamily = font.stack.split(',')[0].trim();
    return Promise.all([
      document.fonts.load(`400 16px ${primaryFamily}`),
      document.fonts.load(`600 16px ${primaryFamily}`),
    ]).catch(() => undefined);
  }

  /**
   * Charge à la demande la feuille de style Google Fonts pour une famille
   * donnée (une seule fois par famille). Pas de self-hosting pour rester
   * simple : seule la police effectivement choisie par l'utilisateur est
   * téléchargée, jamais les 4 en avance.
   */
  private ensureGoogleFontLoaded(googleFamily: string): void {
    if (this.loadedGoogleFamilies.has(googleFamily)) return;
    this.loadedGoogleFamilies.add(googleFamily);

    if (!document.querySelector('link[data-google-fonts-preconnect]')) {
      const preconnectGoogle = document.createElement('link');
      preconnectGoogle.rel = 'preconnect';
      preconnectGoogle.href = 'https://fonts.googleapis.com';
      preconnectGoogle.setAttribute('data-google-fonts-preconnect', '');
      document.head.appendChild(preconnectGoogle);

      const preconnectStatic = document.createElement('link');
      preconnectStatic.rel = 'preconnect';
      preconnectStatic.href = 'https://fonts.gstatic.com';
      preconnectStatic.crossOrigin = 'anonymous';
      document.head.appendChild(preconnectStatic);
    }

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = `https://fonts.googleapis.com/css2?family=${googleFamily}&display=swap`;
    document.head.appendChild(stylesheet);
  }

  private persistFont(id: AppFontId): void {
    const user = this.authService.user.value;
    if (user?.config) {
      user.config = { ...user.config, [this.userConfigKey]: id };
      this.authService.cacheUser(user);
    }

    this.http.patch('api/users/config/', {
      [this.userConfigKey]: id,
    }, { withCredentials: true }).subscribe({
      error: (error) => console.warn('[FontService] Impossible de sauvegarder la police:', error),
    });
  }
}
